<script lang="ts">
  import Color from '../../../../components/color-picker/Color.svelte'
  import Separator from '../../../../components/separator/Separator.svelte'
  import Tooltip from '../../../../components/tooltip/Tooltip.svelte'
  import ZoneSettings from './ZoneSettings/ZoneSettings.svelte'
  import Transform from './Transform/Transform.svelte'
  import { getPrinterColor, getPrinterId } from '../../../../helpers/printer'
  import Export from './Export/Export.svelte'
  import useCanvasSelection from '../../../../hooks/canvas-selection'
  import PrintSettings from './PrintSettings/PrintSettings.svelte'
  import EmptyState from '../../../../components/empty-state/EmptyState.svelte'
  import i18n from '../../../../i18n'
  import { featureFlags } from '../../../../stores/feature-flags'
  import { LifeBuoy, SquareDashedMousePointer } from '@lucide/svelte'
  import PrinterAssignment from './PrinterAssignment/PrinterAssignment.svelte'
  import BatchPrinterAssignment from './PrinterAssignment/BatchPrinterAssignment.svelte'
  import PositioningAssistant from './PositioningAssistant/PositioningAssistant.svelte'
  import NavbarUserMenu from '../../../../components/navbar-user-menu/NavbarUserMenu.svelte'
  import PrinterSettings from './PrinterSettings/PrinterSettings.svelte'

  const { object, multiSelection, ...selection } = useCanvasSelection()
</script>

{#key [$object?.uuid, $multiSelection.map(item => item.uuid).join()].join()}
  <aside class="container">
    <div class="head">
      <Tooltip title={$i18n.t('navbar.documentation.tooltip')}>
        <a
          class="navbar-button"
          href="https://docs.studio.constructions-3d.com"
          target="_blank"
        >
          <LifeBuoy />
        </a>
      </Tooltip>
      <NavbarUserMenu />
    </div>
    {#if selection.hasMultipleItems()}
      <div class="top-container">
        <BatchPrinterAssignment objectIds={selection.getSelectedFileIds()} />
      </div>
    {:else if $object}
      <div class="top-container">
        {#if !selection.hasSingleForbiddenZone()}
          <Tooltip placement="left" title={$object.name}>
            <div class="name">
              {#if selection.hasSinglePrinter()}
                <Color color={getPrinterColor($object)} size={0.875} />
              {/if}
              <p>{$object.name}</p>
            </div>
          </Tooltip>
          <Separator />
        {/if}
        <Transform object={$object} />
        {#if selection.hasSinglePrinter()}
          <Separator />
          <ZoneSettings printerId={$object.uuid} />
          <PrinterSettings printerId={$object.uuid} />
        {/if}
        {#if selection.hasSingleFile() || selection.hasSingleSoloGroup()}
          {@const fileObjectId = (selection.hasSingleSoloGroup() ? selection.getSelectedFileIds()[0] : $object.uuid) as string}
          <Separator />
          <PrinterAssignment objectId={fileObjectId} />
          <Separator />
          <PrintSettings objectId={fileObjectId} />
          <Separator />
          <PositioningAssistant objectId={fileObjectId} />
        {/if}
        {#if selection.hasSingleGroup() && !selection.hasSingleSoloGroup()}
          <Separator />
          <BatchPrinterAssignment objectIds={selection.getSelectedFileIds()} />
        {/if}
        {#if selection.hasSinglePrinter()}
          <Separator />
          <PositioningAssistant objectId={$object.uuid} />
        {/if}
        {#if $featureFlags.developerMode}
          <Separator />
          <pre
            style="padding: 1rem; font-size: 0.75rem; overflow: auto;">{JSON.stringify(
              $object.userData,
              null,
              2,
            )}</pre>
        {/if}
      </div>
      {#if selection.hasSinglePrinter()}
        <div class="bottom-container">
          <Export printerId={getPrinterId($object)} />
        </div>
      {/if}
    {:else}
      <div class="empty">
        <EmptyState
          icon={SquareDashedMousePointer}
          subtitle={$i18n.t('project.rightBar.emptyState.subtitle')}
          title={$i18n.t('project.rightBar.emptyState.title')}
        />
      </div>
    {/if}
  </aside>
{/key}

<style lang="scss">
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  .container {
    align-items: stretch;
    border-left: 1px solid var(--grid-stroke);
    display: flex;
    flex-direction: column;
    flex: 0 0 17rem;
    overflow-y: hidden;

    .head {
      display: flex;
      align-items: center;
      min-height: 4rem;
      border-bottom: 1px solid var(--grid-stroke);

      a {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 4rem;
        min-height: 4rem;
        width: 4rem;
        height: 4rem;
        color: var(--button-ghost-foreground);
        background-color: var(--button-ghost-background);
        border-right: 1px solid var(--grid-stroke);
      }
    }

    .top-container {
      flex: 1;
      overflow-y: auto;
    }

    .name {
      display: flex;
      gap: 0.25rem;
      align-items: center;
      padding: 1rem;

      p {
        font-size: 0.75rem;
        font-weight: 00;
        line-height: 150%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }
  }
</style>
